import { useMemo, useRef, useState, type KeyboardEvent, type ClipboardEvent } from "react";
import clsx from "clsx";
import { FieldRow } from "@/components/ui/Field";
import type { ReactNode } from "react";

export type EmailChipSuggestion = {
  email: string;
  label?: string | null;
  source?: string | null;
};

export interface EmailChipInputProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: EmailChipSuggestion[];
  placeholder?: string;
  emptyText?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export function splitEmailTokens(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function normalizeEmailToken(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (!value || !EMAIL_RE.test(value)) return null;
  return value;
}

function suggestionText(item: EmailChipSuggestion): string {
  const email = item.email.trim();
  const label = (item.label || "").trim();
  if (label && label.toLowerCase() !== email.toLowerCase()) {
    return `${label} · ${email}`;
  }
  return email;
}

export function EmailChipInput({
  label,
  hint,
  error,
  value,
  onChange,
  suggestions = [],
  placeholder = "E-Mail eingeben oder suchen …",
  emptyText = "Keine Treffer — Enter zum Übernehmen",
  id,
  className,
  disabled,
}: EmailChipInputProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimer = useRef<number | null>(null);

  const chosen = useMemo(() => new Set(value.map((v) => v.toLowerCase())), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return suggestions
      .filter((s) => {
        const email = s.email.trim().toLowerCase();
        if (!email || chosen.has(email)) return false;
        if (!q) return true;
        const label = (s.label || "").toLowerCase();
        const source = (s.source || "").toLowerCase();
        return email.includes(q) || label.includes(q) || source.includes(q);
      })
      .slice(0, 12);
  }, [suggestions, query, chosen]);

  function addEmails(rawList: string[]) {
    if (disabled) return;
    const next = [...value];
    const seen = new Set(chosen);
    for (const raw of rawList) {
      const email = normalizeEmailToken(raw);
      if (!email || seen.has(email)) continue;
      seen.add(email);
      next.push(email);
    }
    if (next.length !== value.length) onChange(next);
    setQuery("");
    setHighlight(0);
  }

  function addSuggestion(item: EmailChipSuggestion) {
    addEmails([item.email]);
    inputRef.current?.focus();
  }

  function commitQuery(): boolean {
    const trimmed = query.trim();
    if (!trimmed) return false;
    const exact = normalizeEmailToken(trimmed);
    if (exact && !/[,;\s]/.test(trimmed)) {
      addEmails([exact]);
      return true;
    }
    if (filtered[highlight]) {
      addSuggestion(filtered[highlight]);
      return true;
    }
    const parts = splitEmailTokens(trimmed);
    const candidates = parts.length ? parts : [trimmed];
    if (!candidates.some((p) => normalizeEmailToken(p))) return false;
    addEmails(candidates);
    return true;
  }

  function remove(email: string) {
    if (disabled) return;
    onChange(value.filter((v) => v.toLowerCase() !== email.toLowerCase()));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !query && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
      return;
    }
    if (e.key === "ArrowDown" && filtered.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % filtered.length);
      return;
    }
    if (e.key === "ArrowUp" && filtered.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab" || e.key === "," || e.key === ";") {
      if (!query.trim() && e.key !== "Enter") return;
      if (!query.trim()) return;
      e.preventDefault();
      commitQuery();
      setOpen(true);
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text/plain");
    if (!text || !/[,;\n]/.test(text)) return;
    e.preventDefault();
    addEmails(splitEmailTokens(`${query}${text}`));
  }

  return (
    <FieldRow label={label} hint={hint} error={error} fieldId={id} className={className}>
      <div className="relative">
        <div
          className={clsx(
            "flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-separator bg-card px-2 py-1 shadow-sm",
            "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft",
            disabled && "opacity-60",
          )}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
            setOpen(true);
          }}
        >
          {value.map((email) => (
            <span
              key={email}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent"
            >
              <span className="truncate">{email}</span>
              <button
                type="button"
                aria-label={`${email} entfernen`}
                disabled={disabled}
                className="rounded-full p-0.5 leading-none text-accent hover:bg-fill-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(email);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="email"
            autoComplete="off"
            disabled={disabled}
            className="min-w-[10rem] flex-1 border-0 bg-transparent p-1 text-sm text-label placeholder:text-label-tertiary focus:outline-none focus:ring-0"
            placeholder={value.length === 0 ? placeholder : ""}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setHighlight(0);
            }}
            onFocus={() => {
              if (blurTimer.current) window.clearTimeout(blurTimer.current);
              setOpen(true);
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => {
                if (query.trim()) commitQuery();
                setOpen(false);
              }, 140);
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
        </div>
        {open && !disabled && (query.trim() || filtered.length > 0) ? (
          <ul className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-label-tertiary">
                {normalizeEmailToken(query) ? `Enter: ${query.trim().toLowerCase()} übernehmen` : emptyText}
              </li>
            ) : (
              filtered.map((item, idx) => (
                <li key={`${item.email}-${idx}`}>
                  <button
                    type="button"
                    className={clsx(
                      "block w-full px-3 py-1.5 text-left hover:bg-fill-tertiary",
                      idx === highlight && "bg-fill-tertiary",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addSuggestion(item);
                    }}
                    onMouseEnter={() => setHighlight(idx)}
                  >
                    <span className="block truncate text-label">{suggestionText(item)}</span>
                    {item.source ? (
                      <span className="block truncate text-xs text-label-tertiary">{item.source}</span>
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </FieldRow>
  );
}
